import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Divider,
  List,
  ListItem,
  Avatar,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ArticleOutlined as ArticleIcon,
  InfoOutlined as InfoIcon,
  CodeOutlined as CodeIcon,
  DesignServicesOutlined as DesignIcon,
  SecurityOutlined as SecurityIcon,
  ApiOutlined as ApiIcon,
  ViewListOutlined as FeaturesIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import conversationService from '../../services/conversationService';
import useNotification from '../../hooks/useNotification';
import useDocuments from '../../hooks/useDocuments';
import grootImageUrl from '../../assets/images/groot.jpg';

// Custom styled Groot avatar
const GrootAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: '#4d9559',
  width: 32,
  height: 32,
  border: `1px solid ${theme.palette.common.white}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
}));

// Groot image is imported at the top of the file

/**
 * A Groot-themed chat interface component for the PRD workflow
 * Provides streaming chat functionality when generating content
 */
const GrootWorkflowChat = ({ 
  sectionName,
  projectId, 
  existingContent = '',
  onSaveContent,
  disabled = false,
  placeholder = "Ask Groot about this section...",
  relatedSections = {} // Contains content from related sections
}) => {
  // State for chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showRelatedSections, setShowRelatedSections] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { showNotification } = useNotification();
  const { createDocument, saveMdFile, getProjectDocuments } = useDocuments();

  // Reset messages when the section changes
  useEffect(() => {
    // Clear messages when switching sections
    setMessages([]);
    setStreamingResponse('');
    
    // If there's existing content, show it as a system message
    if (existingContent) {
      setMessages([{
        role: 'system',
        content: `Current content for ${sectionName}:\n\n${existingContent}`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [existingContent, sectionName]);

  // Create a conversation if needed
  useEffect(() => {
    if (!conversationId && projectId && !disabled) {
      const createNewConversation = async () => {
        try {
          // Get task_id from URL parameters if available
          const urlSearchParams = new URLSearchParams(window.location.search);
          const taskId = urlSearchParams.get('task');
          
          // Use optimized get-or-create endpoint
          const newConversation = await conversationService.getOrCreateConversation(
            projectId,
            taskId,
            `${sectionName} Generation with Groot`
          );
          setConversationId(newConversation._id);
        } catch (error) {
          console.error('Error creating conversation:', error);
          showNotification({
            message: `I am Groot! (Error creating conversation: ${error.message})`,
            type: 'error'
          });
        }
      };
      
      createNewConversation();
    }
  }, [conversationId, projectId, sectionName, disabled, showNotification]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingResponse]);

  // Translate Groot responses
  const translateToGroot = (message) => {
    // Here you could add logic to convert normal responses to Groot-style responses
    // For simplicity, we'll just prepend "I am Groot! " to messages
    if (!message) return message;

    // For technical content, keep it readable but add Groot's touch
    // by adding a Groot prefix to each paragraph
    const paragraphs = message.split('\n\n');
    const grootParagraphs = paragraphs.map((para, index) => {
      // Skip Groot-ifying code blocks, lists and tables
      if (
        para.includes('```') ||
        para.includes('|') ||
        para.match(/^\d+\.\s/) ||
        para.match(/^-\s/) ||
        para.match(/^#/)
      ) {
        return para;
      }
      
      // Add Groot's phrase to the first paragraph
      if (index === 0) {
        return `I am Groot! (${para})`;
      }
      // For long responses, mix in some Groot phrases randomly
      else if (paragraphs.length > 3 && index % 3 === 0) {
        return `I am Groot. (${para})`;
      }
      return para;
    });
    
    return grootParagraphs.join('\n\n');
  };

  // Handle sending new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversationId || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: newMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(messages => [...messages, userMessage]);
    setNewMessage('');
    setIsLoading(true);
    setIsStreaming(true);
    
    try {
      // Clear any previous streaming response
      setStreamingResponse('');
      
      // Get the section type (normalize it for API)
      const sectionType = sectionName;
      console.log(`Using context-aware streaming for section: ${sectionType}`);
      
      // Stream the message with context from related sections
      await conversationService.streamMessageWithContext(
        conversationId,
        newMessage,
        (chunk, fullText) => {
          // Update streaming response as chunks arrive
          setStreamingResponse(prev => translateToGroot(prev + chunk));
        },
        (completeResponse) => {
          // When streaming is complete, add the full response to messages
          const assistantMessage = {
            role: 'assistant',
            content: translateToGroot(completeResponse),
            timestamp: new Date().toISOString()
          };
          setMessages(messages => [...messages, assistantMessage]);
          setStreamingResponse('');
          setIsStreaming(false);
          setIsLoading(false);
        },
        (error) => {
          console.error('Streaming error:', error);
          showNotification({
            message: `I AM GROOT! (Error: ${error.message})`,
            type: 'error'
          });
          setIsStreaming(false);
          setIsLoading(false);
        },
        sectionType,
        relatedSections
      );
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification({
        message: `I AM GROOT! (Error sending message: ${error.message})`,
        type: 'error'
      });
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  // Handle save content from chat
  const handleSaveContent = async () => {
    // Get all assistant messages
    const assistantMessages = messages
      .filter(msg => msg.role === 'assistant')
      .map(msg => msg.content);
    
    let contentToSave = '';
    
    // Select appropriate content based on section type
    if (streamingResponse && streamingResponse.trim() !== '') {
      // If there's a streaming response in progress, use that but remove Groot phrases
      contentToSave = streamingResponse.replace(/I am Groot[!?.]* \((.*?)\)/g, '$1');
      console.log('Using streaming response for save content');
    } else if (assistantMessages.length > 0) {
      // First check the last message as it's most likely to have the relevant content
      const lastMessage = assistantMessages[assistantMessages.length - 1];
      contentToSave = lastMessage.replace(/I am Groot[!?.]* \((.*?)\)/g, '$1');
    }
    
    // Save the content
    if (contentToSave && onSaveContent) {
      try {
        console.log(`Saving content for ${sectionName}, length: ${contentToSave.length} characters`);
        
        // First, save content via the parent handler (updates UI state)
        await onSaveContent(contentToSave);
        
        // Then create document in the backend database
        try {
          if (!projectId) {
            throw new Error('Project ID is required to save document');
          }
          
          // Map section names to valid document types
          let documentType;
          switch (sectionName) {
            case 'Field Validations':
              documentType = 'validation';
              break;
            case 'Technology Stack':
              documentType = 'tech_stack';
              break;
            case 'Development Guidelines':
              documentType = 'dev_guidelines';
              break;
            case 'Design Guidelines':
              documentType = 'design_guidelines';
              break;
            case 'Security Guidelines':
              documentType = 'security_guidelines';
              break;
            case 'API Guidelines':
              documentType = 'api_guidelines';
              break;
            case 'Features':
              documentType = 'features';
              break;
            case 'User Stories':
              documentType = 'user_stories';
              break;
            case 'Final PRD':
              documentType = 'final_prd';
              break;
            default:
              documentType = sectionName.toLowerCase().replace(/\s+/g, '_');
          }
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const documentTitle = `${sectionName} - ${timestamp}`;
          
          // First create document record in database
          const newDocument = await createDocument(projectId, {
            title: documentTitle,
            content: contentToSave,
            type: documentType
          });
          
          console.log('Created document:', newDocument);
          
          // Also save as markdown file
          const filename = `${sectionName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.md`;
          const savedFile = await saveMdFile(projectId, filename, contentToSave);
          
          console.log('Saved markdown file:', savedFile);
          
          showNotification({
            message: `I AM GROOT! (${sectionName} content saved successfully!)`,
            type: 'success'
          });
        } catch (docError) {
          console.error('Error creating document file:', docError);
          showNotification({
            message: `I am Groot... (Content saved but couldn't create document file: ${docError.message})`,
            type: 'warning'
          });
        }
      } catch (error) {
        console.error('Error saving content:', error);
        showNotification({
          message: `I AM GROOT! (Error saving content: ${error.message})`,
          type: 'error'
        });
      }
    } else {
      showNotification({
        message: `I am Groot? (No content to save)`,
        type: 'warning'
      });
    }
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Get section-specific prompt with Groot flavor
  const getSectionPrompt = () => {
    // Custom prompts for specific sections with Groot flavor
    const sectionPrompts = {
      "Field Validations": 
        `I am Groot! (I'll help you define validation rules based on the PRD.) ${
          existingContent 
            ? "I am Groot. (I've loaded the current validation rules above for reference.)" 
            : "I am Groot? (Shall we analyze the PRD to identify fields that need validation?)"
        }`,
      
      "Technology Stack": 
        `I am Groot! (Let's define the appropriate technology stack based on both the PRD and Field Validations.) ${
          existingContent 
            ? "I am Groot. (I've loaded the current technology recommendations above for reference.)" 
            : "I am Groot? (What specific requirements or constraints should we consider?)"
        }`,
      
      "Development Guidelines": 
        `I am Groot! (I'll help create comprehensive development guidelines based on the PRD and Technology Stack.) ${
          existingContent 
            ? "I am Groot. (I've loaded the current content above for reference.)" 
            : "I am Groot? (What specific development aspects would you like to focus on?)"
        }`,
      
      "Final PRD": 
        `I am Groot! (I'll help compile a comprehensive final PRD that integrates all previous sections.) ${
          existingContent 
            ? "I am Groot. (I've loaded the current content above for reference.)" 
            : "I am Groot? (Shall we create individual .md files for each section?)"
        }`
    };
    
    // Return custom prompt or default prompt
    return sectionPrompts[sectionName] || 
      `I am Groot! (I'll help you generate content for the ${sectionName} section.) ${
        existingContent 
          ? "I am Groot. (I've loaded the current content above so you can see what's already there.)" 
          : "I am Groot? (What would you like to include?)"
      }`;
  };
  
  // Get the appropriate initial prompt
  const initialPrompt = getSectionPrompt();
  
  // Get relevant section dependencies based on current section
  const getRelevantSections = () => {
    switch (sectionName) {
      case 'Design Guidelines':
        return ['Field Validations', 'Technology Stack'];
        
      case 'Security Guidelines':
        return ['Technology Stack', 'Development Guidelines'];
        
      case 'API Guidelines':
        return ['Technology Stack', 'Development Guidelines'];
        
      case 'Features':
        return [
          'Field Validations', 
          'Technology Stack', 
          'Development Guidelines', 
          'Design Guidelines', 
          'Security Guidelines', 
          'API Guidelines'
        ];
        
      case 'User Stories':
        return [
          'Features',
          'Field Validations', 
          'Technology Stack', 
          'Development Guidelines', 
          'Design Guidelines', 
          'Security Guidelines', 
          'API Guidelines'
        ];
        
      case 'Final PRD':
        return [
          'Features',
          'User Stories',
          'Field Validations', 
          'Technology Stack', 
          'Development Guidelines', 
          'Design Guidelines', 
          'Security Guidelines', 
          'API Guidelines'
        ];
        
      default:
        return [];
    }
  };
  
  // Get section icon based on name
  const getSectionIcon = (sectionName) => {
    switch (sectionName) {
      case 'Field Validations':
        return <ArticleIcon />;
      case 'Technology Stack':
        return <CodeIcon />;
      case 'Development Guidelines':
        return <InfoIcon />;
      case 'Design Guidelines':
        return <DesignIcon />;
      case 'Security Guidelines':
        return <SecurityIcon />;
      case 'API Guidelines':
        return <ApiIcon />;
      case 'Features':
        return <FeaturesIcon />;
      case 'User Stories':
        return <PersonIcon />;
      case 'Final PRD':
        return <ArticleIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(to bottom, rgba(46, 125, 50, 0.05), rgba(46, 125, 50, 0))'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GrootAvatar src={grootImageUrl} sx={{ mr: 1 }} />
          <Typography variant="h6">Groot {sectionName} Assistant</Typography>
        </Box>
        <Tooltip title="Save content from this chat">
          <span>
            <Button
              startIcon={<SaveIcon />}
              variant="outlined"
              onClick={handleSaveContent}
              disabled={disabled || (!streamingResponse && messages.filter(m => m.role === 'assistant').length === 0)}
              sx={{ 
                borderColor: '#4d9559',
                color: '#2e7d32',
                '&:hover': {
                  borderColor: '#2e7d32',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)'
                }
              }}
            >
              Save Content
            </Button>
          </span>
        </Tooltip>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      {/* Related sections panel - only show if there are relevant sections */}
      {getRelevantSections().length > 0 && showRelatedSections && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              I am Groot (Related Sections Reference)
            </Typography>
            <Chip 
              label={showRelatedSections ? "Hide References" : "Show References"}
              size="small"
              onClick={() => setShowRelatedSections(!showRelatedSections)}
              color="primary"
              variant="outlined"
              sx={{ 
                borderColor: '#4d9559',
                color: '#2e7d32'
              }}
            />
          </Box>
          
          {getRelevantSections().map((section) => (
            <Accordion 
              key={section}
              expanded={expandedSection === section}
              onChange={() => setExpandedSection(expandedSection === section ? null : section)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#4d9559', mr: 1, width: 24, height: 24 }}>
                    {getSectionIcon(section)}
                  </Avatar>
                  <Typography variant="body2">{section}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: '150px', overflow: 'auto' }}>
                  {relatedSections[section] ? (
                    <ReactMarkdown>
                      {relatedSections[section]}
                    </ReactMarkdown>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">
                      I am Groot... (No content available for {section})
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      
      {/* Chat messages area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        maxHeight: '400px',
        mb: 2,
        backgroundColor: 'rgba(46, 125, 50, 0.04)',
        borderRadius: 1,
        p: 2
      }}>
        {/* System prompt for context */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <GrootAvatar src={grootImageUrl} sx={{ mr: 1, width: 32, height: 32 }} />
            <Typography variant="body2" color="text.secondary">
              {initialPrompt}
            </Typography>
          </Box>
          
          {/* Show reference toggle when there are relevant sections */}
          {getRelevantSections().length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                size="small"
                variant="text" 
                onClick={() => setShowRelatedSections(!showRelatedSections)}
                sx={{ color: '#4d9559' }}
              >
                {showRelatedSections ? "Hide References" : "Show References"}
              </Button>
            </Box>
          )}
        </Box>
        
        {/* Message list */}
        <List sx={{ p: 0 }}>
          {messages.filter(msg => msg.role !== 'system').map((message, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                p: 1
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'flex-start',
                maxWidth: '80%'
              }}>
                {message.role === 'assistant' && (
                  <GrootAvatar src={grootImageUrl} sx={{ mr: 1, width: 32, height: 32 }} />
                )}
                
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2,
                    bgcolor: message.role === 'user' ? 'primary.light' : 'white',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <ReactMarkdown>
                    {message.content}
                  </ReactMarkdown>
                </Paper>
                
                {message.role === 'user' && (
                  <Avatar sx={{ bgcolor: 'secondary.main', ml: 1, width: 32, height: 32 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
              </Box>
            </ListItem>
          ))}
          
          {/* Streaming response */}
          {isStreaming && streamingResponse && (
            <ListItem 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'flex-start',
                p: 1
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'flex-start',
                maxWidth: '80%'
              }}>
                <GrootAvatar src={grootImageUrl} sx={{ mr: 1, width: 32, height: 32 }} />
                
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2,
                    bgcolor: 'white',
                    color: 'text.primary',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <ReactMarkdown>
                    {streamingResponse}
                  </ReactMarkdown>
                </Paper>
              </Box>
            </ListItem>
          )}
          
          {/* Auto-scroll anchor */}
          <div ref={messagesEndRef} />
        </List>
      </Box>
      
      {/* Input area */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder={placeholder}
          variant="outlined"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading || disabled}
          inputRef={inputRef}
          multiline
          rows={2}
          sx={{ 
            mr: 1,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(77, 149, 89, 0.5)',
              },
              '&:hover fieldset': {
                borderColor: '#4d9559',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#2e7d32',
              },
            },
          }}
        />
        
        <IconButton 
          color="primary" 
          onClick={handleSendMessage}
          disabled={!newMessage.trim() || isLoading || disabled}
          size="large"
          sx={{ 
            color: '#2e7d32',
            '&:hover': {
              backgroundColor: 'rgba(46, 125, 50, 0.04)'
            }
          }}
        >
          {isLoading ? <CircularProgress size={24} sx={{ color: '#4d9559' }} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default GrootWorkflowChat;